import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reorder } from 'motion/react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { blogCategories, BlogCategory, ContentBlockType } from '../../lib/blog-types';
import { API_ORIGIN } from '../../lib/apiBase';
import { Segmented } from './dash/Toolbar';
import { CanvasTitle, EditorShell, EditorStatus, useDirty } from './editor/EditorShell';
import { AutoTextarea, Field, ImageField, InspectorSection, Select, TagInput, TextInput, Toggle } from './editor/fields';
import { BlockCard, BlockMenu, ContentBlock, InsertPoint, templateFor } from './blog/blocks';

const API_BASE = process.env.NODE_ENV === 'production' ? `${API_ORIGIN}/api/blog` : `${API_ORIGIN}/.netlify/functions/blog`;

interface Author {
  id: string;
  name: string;
  role?: string;
}

interface BlogPostData {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  tags: string[];
  featuredImage?: string;
  iconName?: string;
  authorId?: string;
  publishedAt?: string;
  readTime: number;
  featured: boolean;
  status: EditorStatus;
  blocks: ContentBlock[];
}

const EMPTY: BlogPostData = {
  slug: '',
  title: '',
  excerpt: '',
  category: 'design-tips',
  tags: [],
  featuredImage: '',
  iconName: '',
  authorId: '',
  readTime: 5,
  featured: false,
  status: 'draft',
  blocks: [],
};

const slugify = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const newId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Rough read time from the words in text-bearing blocks (200 wpm). */
const estimateReadTime = (blocks: ContentBlock[]) => {
  const text = blocks
    .map((b) => {
      if (b.blockType === 'text') return b.content;
      try {
        const d = JSON.parse(b.content);
        return [d.text, d.title, d.left, d.right, ...(d.items || [])].filter(Boolean).join(' ');
      } catch {
        return '';
      }
    })
    .join(' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

interface Props {
  postId?: string;
  /** Leave the editor (back to the list). */
  onCancel?: () => void;
  /** Called after every successful save. */
  onSave?: () => void;
  /** Called once when a new post is first saved, with its id. */
  onCreated?: (id: string) => void;
}

const BlogPostEditor: React.FC<Props> = ({ postId, onCancel, onSave, onCreated }) => {
  const navigate = useNavigate();
  const [doc, setDoc] = useState<BlogPostData>(EMPTY);
  const [saved, setSaved] = useState<string | null>(postId ? null : JSON.stringify(EMPTY));
  const [slugTouched, setSlugTouched] = useState(!!postId);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(!!postId);
  const [saving, setSaving] = useState(false);
  const dirty = useDirty(doc, saved);

  const token = () => localStorage.getItem('admin_token');
  const leave = onCancel || (() => navigate('/admin/blog'));

  useEffect(() => {
    fetch(`${API_BASE}/authors`)
      .then((r) => r.json())
      .then((d) => d.success && setAuthors(d.authors))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/admin/posts`, { headers: { Authorization: `Bearer ${token()}` } });
        if (res.status === 401) return navigate('/admin/login', { replace: true });
        const data = await res.json();
        const post = data.success ? data.posts.find((p: { id: string }) => p.id === postId) : null;
        if (!post) throw new Error('Post not found');
        const loaded: BlogPostData = {
          id: post.id,
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt || '',
          category: post.category,
          tags: post.tags || [],
          featuredImage: post.featuredImage || '',
          iconName: post.iconName || '',
          authorId: post.authorId || '',
          publishedAt: post.publishedAt,
          readTime: post.readTime || 5,
          featured: post.featured || false,
          status: post.status === 'published' ? 'published' : 'draft',
          blocks: (post.blocks || []).map((b: ContentBlock, i: number) => ({
            id: b.id || newId(),
            blockType: b.blockType,
            content: b.content,
            sortOrder: b.sortOrder ?? i,
            metadata: b.metadata,
          })),
        };
        if (!cancelled) {
          setDoc(loaded);
          setSaved(JSON.stringify(loaded));
        }
      } catch (err: any) {
        toast.error(err?.message || 'Could not load the post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postId, navigate]);

  const set = <K extends keyof BlogPostData>(key: K, value: BlogPostData[K]) => setDoc((d) => ({ ...d, [key]: value }));

  const save = useCallback(
    async (publish: boolean) => {
      if (!doc.title.trim()) {
        toast.error('Give the post a title first');
        return;
      }
      setSaving(true);
      try {
        const status: EditorStatus = publish ? 'published' : doc.status;
        const next: BlogPostData = {
          ...doc,
          slug: doc.slug || slugify(doc.title),
          status,
          publishedAt: status === 'published' && !doc.publishedAt ? new Date().toISOString() : doc.publishedAt,
        };
        const payload = {
          ...next,
          blocks: next.blocks.map((b, i) => ({ type: b.blockType, content: b.content, sortOrder: i, metadata: b.metadata })),
        };
        const res = await fetch(postId ? `${API_BASE}/admin/posts/${postId}` : `${API_BASE}/admin/posts`, {
          method: postId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify(payload),
        });
        if (res.status === 401) return navigate('/admin/login', { replace: true });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Could not save the post');
        setDoc(next);
        setSaved(JSON.stringify(next));
        toast.success(publish && doc.status !== 'published' ? 'Post published' : 'Post saved');
        onSave?.();
        if (!postId && data.post?.id) onCreated?.(data.post.id);
      } catch (err: any) {
        toast.error(err?.message || 'Could not save the post');
      } finally {
        setSaving(false);
      }
    },
    [doc, postId, navigate, onSave, onCreated]
  );

  const insertBlock = (type: ContentBlockType, at: number) => {
    const t = templateFor(type);
    if (!t) return;
    const block: ContentBlock = { id: newId(), blockType: type, content: t.defaultContent, sortOrder: at };
    setDoc((d) => ({ ...d, blocks: [...d.blocks.slice(0, at), block, ...d.blocks.slice(at)] }));
  };
  const updateBlock = (id: string, content: string) => setDoc((d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === id ? { ...b, content } : b)) }));
  const deleteBlock = (id: string) => setDoc((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== id) }));
  const moveBlock = (index: number, delta: -1 | 1) =>
    setDoc((d) => {
      const to = index + delta;
      if (to < 0 || to >= d.blocks.length) return d;
      const blocks = [...d.blocks];
      [blocks[index], blocks[to]] = [blocks[to], blocks[index]];
      return { ...d, blocks };
    });

  const categoryLabel = blogCategories.find((c) => c.value === doc.category)?.label;
  const estimate = estimateReadTime(doc.blocks);

  const inspector = (
    <>
      <InspectorSection title="Status">
        <Segmented<EditorStatus>
          value={doc.status}
          onChange={(v) => set('status', v)}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'published', label: 'Published' },
          ]}
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
        />
        <Toggle checked={doc.featured} onChange={(v) => set('featured', v)} label="Featured post" hint="Shown first on the blog page." />
        {doc.publishedAt ? (
          <p className="text-[11.5px] text-[color:var(--oa-muted)]">
            First published {new Date(doc.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        ) : null}
      </InspectorSection>

      <InspectorSection title="Post details">
        <Field label="URL" hint={slugTouched ? undefined : 'Follows the title until you edit it.'}>
          <TextInput
            prefix="/blog/"
            value={doc.slug}
            onChange={(e) => {
              setSlugTouched(true);
              set('slug', slugify(e.target.value));
            }}
            placeholder="post-url"
          />
        </Field>
        <Field label="Category">
          <Select value={doc.category} onChange={(e) => set('category', e.target.value as BlogCategory)}>
            {blogCategories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Author">
          <Select value={doc.authorId || ''} onChange={(e) => set('authorId', e.target.value || undefined)}>
            <option value="">No author</option>
            {authors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Read time"
          hint={
            doc.blocks.length > 0 && estimate !== doc.readTime ? (
              <>
                Content reads in about {estimate} min.{' '}
                <button type="button" onClick={() => set('readTime', estimate)} className="font-medium text-[color:var(--oa-accent)] hover:underline">
                  Use {estimate}
                </button>
              </>
            ) : undefined
          }
        >
          <div className="flex items-center gap-2">
            <TextInput type="number" min={1} value={doc.readTime} onChange={(e) => set('readTime', parseInt(e.target.value, 10) || 1)} className="w-20" />
            <span className="text-[12.5px] text-[color:var(--oa-muted)]">minutes</span>
          </div>
        </Field>
        <Field label="Tags">
          <TagInput value={doc.tags} onChange={(v) => set('tags', v)} />
        </Field>
      </InspectorSection>

      <InspectorSection title="Featured image">
        <ImageField value={doc.featuredImage || ''} onChange={(v) => set('featuredImage', v)} />
        <Field label="Fallback icon" hint="Lucide icon name shown when there is no image, e.g. Palette.">
          <TextInput value={doc.iconName || ''} onChange={(e) => set('iconName', e.target.value)} placeholder="FileText" />
        </Field>
      </InspectorSection>
    </>
  );

  return (
    <EditorShell
      section="Blog"
      kind="Post"
      title={doc.title}
      status={doc.status}
      dirty={dirty}
      saving={saving}
      loading={loading}
      previewHref={postId && doc.slug ? `/blog/${doc.slug}` : undefined}
      onBack={leave}
      onSaveDraft={() => save(false)}
      onPublish={() => save(true)}
      inspector={inspector}
    >
      <CanvasTitle
        eyebrow={categoryLabel}
        title={doc.title}
        onTitle={(title) => setDoc((d) => ({ ...d, title, slug: slugTouched ? d.slug : slugify(title) }))}
        titlePlaceholder="Post title"
        sub={
          <AutoTextarea
            bare
            minRows={2}
            value={doc.excerpt}
            onChange={(e) => set('excerpt', e.target.value)}
            placeholder="Standfirst: one or two sentences shown on the blog list."
            className="mt-3 text-[16px] leading-7 text-[color:var(--oa-muted)]"
          />
        }
      />

      {doc.blocks.length ? (
        <Reorder.Group as="div" axis="y" values={doc.blocks} onReorder={(blocks) => set('blocks', blocks)}>
          {doc.blocks.map((b, i) => (
            <React.Fragment key={b.id}>
              <InsertPoint onPick={(type) => insertBlock(type, i)} />
              <BlockCard block={b} index={i} total={doc.blocks.length} onChange={(c) => updateBlock(b.id, c)} onMove={(d) => moveBlock(i, d)} onDelete={() => deleteBlock(b.id)} />
            </React.Fragment>
          ))}
        </Reorder.Group>
      ) : null}

      <div className="mt-4">
        <BlockMenu onPick={(type) => insertBlock(type, doc.blocks.length)}>
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[color:var(--oa-line-3)] bg-[color:color-mix(in_oklab,var(--oa-card)_60%,transparent)] py-5 text-[13px] font-medium text-[color:var(--oa-muted)] transition-colors hover:border-[color:var(--oa-accent)] hover:bg-[color:var(--oa-card)] hover:text-[color:var(--oa-accent)]"
          >
            <Plus className="h-4 w-4" />
            {doc.blocks.length ? 'Add a block' : 'Start writing: add the first block'}
          </button>
        </BlockMenu>
      </div>
    </EditorShell>
  );
};

export default BlogPostEditor;
